<template>
    <div v-if="stats.length" ref="root" :class="['grid gap-8 sm:gap-6', gridClass]">
        <div v-for="(stat, index) in stats" :key="index" class="text-center">
            <!-- tabular-nums holds every digit at the same width. Without it the
                 number visibly jitters as it counts, because proportional digits
                 are different widths and the line keeps re-centring. -->
            <h2 class="leading-none font-bold text-4xl sm:text-5xl 2xl:text-6xl text-primary tabular-nums">
                {{ formatted[index] }}<span>{{ stat.suffix }}</span>
            </h2>
            <p class="mt-3 leading-none dark:text-white-light">{{ stat.label }}</p>
        </div>
    </div>
</template>

<script setup>
import { computed, defineProps, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';

const props = defineProps({
    stats: { type: Array, default: () => [] },
    // Long enough to read as a deliberate reveal, short enough that a visitor
    // scrolling past does not miss the number entirely.
    duration: { type: Number, default: 2000 },
});

const root = ref(null);
const current = ref([]);

let frame = 0;
let observer = null;
let started = false;

// Written out in full — Tailwind scans source text, so a class assembled by
// concatenation is never generated.
const GRID = {
    1: 'grid-cols-1',
    2: 'grid-cols-2',
    3: 'grid-cols-1 sm:grid-cols-3',
    4: 'grid-cols-2 lg:grid-cols-4',
};
const gridClass = computed(() => GRID[props.stats.length] || 'grid-cols-1 sm:grid-cols-3');

const formatted = computed(() => current.value.map((n) => Math.round(n).toLocaleString('en-US')));

const targets = () => props.stats.map((s) => Number(s.value) || 0);
const reset = () => { current.value = props.stats.map(() => 0); };
const finish = () => { current.value = targets(); };

/** Decelerating: most of the distance early, then a slow settle onto the number. */
const easeOutCubic = (t) => 1 - (1 - t) ** 3;

function run() {
    if (started || !props.stats.length) return;
    started = true;

    // A visitor who asked for reduced motion gets the figure, not the animation.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        finish();
        return;
    }

    const to = targets();
    const startedAt = performance.now();

    const step = (now) => {
        const t = Math.min(1, (now - startedAt) / props.duration);

        current.value = to.map((value) => value * easeOutCubic(t));

        if (t < 1) {
            frame = requestAnimationFrame(step);
        } else {
            frame = 0;
            // Land on the exact figure rather than whatever the easing rounded to.
            finish();
        }
    };

    frame = requestAnimationFrame(step);
}

/**
 * Counting up while the row is still below the fold wastes the effect — the
 * visitor arrives to a number that has already finished. Wait until it is on
 * screen.
 *
 * Called again whenever the list changes, because the values arrive from the API
 * after mount: on the first render `stats` is empty, the v-if keeps the element
 * out of the DOM, and there is nothing to observe yet.
 */
function attach() {
    if (started || !props.stats.length || !root.value) return;

    if (!('IntersectionObserver' in window)) {
        run();
        return;
    }

    if (observer) observer.disconnect();

    observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
            run();
            observer.disconnect();
            observer = null;
        }
    }, { threshold: 0.35 });

    observer.observe(root.value);
}

onMounted(() => {
    reset();
    nextTick(attach);
});

watch(() => props.stats, () => {
    // Already counted: a later change just snaps to the new figures rather than
    // replaying the animation under the visitor.
    if (started) {
        finish();
        return;
    }

    reset();
    nextTick(attach);
}, { deep: true });

onBeforeUnmount(() => {
    if (frame) cancelAnimationFrame(frame);
    if (observer) observer.disconnect();
});
</script>
